
import React from 'react';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { selectNotifications } from '../../../modules/notifications/selectors';
import { INotification as ReduxNotification } from '../../../modules/notifications/types';
import { markNotificationAsRead, fetchNotifications, deleteNotification, markAllNotificationsAsRead } from '../../../modules/notifications/actions';
import { AppDispatch } from '../../../store';
import { useDispatch } from 'react-redux';
import {
  StyledPopover,
  DrawerHeader,
  HeaderTitle,
  NotificationList,
  NotificationItemContainer,
  ItemContent,
  NotificationMessage,
  TimeText,
  DrawerFooter,
  SeeAllLink,
  MarkAllLink,
  DeleteButton
} from './styles';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import AlertPopup from '../../common/AlertPopup';


interface FacilityNotificationDrawerProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const FacilityNotificationDrawer: React.FC<FacilityNotificationDrawerProps> = ({
  anchorEl,
  open,
  onClose
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useSelector(selectNotifications);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = React.useState<number | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [toast, setToast] = React.useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Take only first 5 notifications for the drawer
  const displayNotifications = notifications.slice(0, 5);


  const getStatusColor = (type: string) => {
    switch (type) {
      case 'shift_assignment':
      case 'shift_accepted':
      case 'success':
        return theme.palette.primary.main; // Green
      case 'worker_no_show':
      case 'warning':
        return theme.palette.warning.main; // Yellow/Orange
      case 'facility_onboarding':
      case 'info':
        return '#2196F3'; // Blue
      case 'shift_cancellation':
      case 'error':
        return theme.palette.error.main; // Red
      default:
        return theme.palette.grey[500];
    }
  };

  const handleMarkAsRead = (event: React.MouseEvent, notificationId: number, isRead: boolean) => {
    event.stopPropagation();
    if (!isRead) {
      dispatch(markNotificationAsRead(notificationId))
        .unwrap()
        .then(() => {
          dispatch(fetchNotifications({ page: 1, page_size: 10 }));
        });
    }
  };

  const handleDeleteClick = (event: React.MouseEvent, notificationId: number) => {
    event.stopPropagation();
    setSelectedNotificationId(notificationId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedNotificationId) {
      setIsDeleting(true);
      dispatch(deleteNotification(selectedNotificationId))
        .unwrap()
        .then(() => {
          setIsDeleting(false);
          setDeleteConfirmOpen(false);
          setToast({ open: true, message: 'Notification deleted successfully!', severity: 'success' });
          dispatch(fetchNotifications({ page: 1, page_size: 10 }));
        })
        .catch((err) => {
          setIsDeleting(false);
          setToast({ open: true, message: err || 'Failed to delete notification', severity: 'error' });
        });
    }
  };

  const handleMarkAllRead = () => {
    dispatch(markAllNotificationsAsRead())
      .unwrap()
      .then(() => {
        dispatch(fetchNotifications({ page: 1, page_size: 10 }));
      })
      .catch((err: any) => {
        console.error('Failed to mark all notifications as read:', err);
      });
  };

  return (
    <>
      <StyledPopover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <DrawerHeader>
          <HeaderTitle>Recent Notifications</HeaderTitle>
          {notifications.some(n => !n.is_read) && (
            <MarkAllLink onClick={handleMarkAllRead}>
              Mark all as read
            </MarkAllLink>
          )}
        </DrawerHeader>

        <NotificationList>
          {displayNotifications.map((notification: ReduxNotification) => (
            <NotificationItemContainer
              key={notification.notification_id}
              statusColor={getStatusColor(notification.notification_type)}
              isRead={notification.is_read}
              onClick={(e) => handleMarkAsRead(e, notification.notification_id, notification.is_read)}
            >
              <ItemContent>
                <NotificationMessage>
                  <span style={{ fontWeight: 'bold', display: 'block' }}>{notification.notification_title}</span>
                  {notification.notification_message}
                </NotificationMessage>
                <TimeText>
                  {new Date(notification.created_on).toLocaleString()}
                </TimeText>
              </ItemContent>
              <DeleteButton onClick={(e) => handleDeleteClick(e, notification.notification_id)}>
                <DeleteIcon sx={{ fontSize: '1.2rem' }} />
              </DeleteButton>
            </NotificationItemContainer>
          ))}
          {displayNotifications.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
              No new notifications
            </div>
          )}
        </NotificationList>

        <DrawerFooter>
          <SeeAllLink
            onClick={() => {
              onClose();
              navigate('/facility/notifications');
            }}
          >
            See All Notifications
          </SeeAllLink>
        </DrawerFooter>
      </StyledPopover>

      <ConfirmationDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Notification"
        message="Are you sure you want to delete this notification?"
        loading={isDeleting}
      />

      <AlertPopup
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
      />
    </>
  );
};

export default FacilityNotificationDrawer;
